import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ElfsightWidget from './ElfsightWidget';
import EnhancedBriefingModal from './EnhancedBriefingModal';
import NewsCard from './NewsCard';
import LogoSvg from './LogoSvg';
import ImageCycler from './ImageCycler';
import '../components.css';

const Landing = ({ authUser, onLogout }) => {
  const navigate = useNavigate();
  const [latestSummary, setLatestSummary] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Helper function to check if briefing should show "Has errors"
  const shouldShowHasErrors = (hasError, error) => {
    if (!hasError || !authUser || !authUser.is_admin) return false;
    
    // If there's no error text, don't show "Has errors"
    if (!error || !error.trim()) return false;
    
    // Filter out "Insufficient articles", "Fewer than", and "all categories" notes
    const filteredError = error
      .split('\n')
      .filter(line => 
        !line.includes('Insufficient articles') && 
        !line.includes('Fewer than') &&
        !line.includes('all categories')
      )
      .join('\n')
      .trim();
    
    return filteredError.length > 0;
  };
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showBriefingModal, setShowBriefingModal] = useState(false);
  const [selectedBriefing, setSelectedBriefing] = useState(null);
  const [briefingData, setBriefingData] = useState(null);
  const [singleStory, setSingleStory] = useState(null);

  useEffect(() => {
    // Fetch the latest summary data for display using the anonymous endpoint
    const fetchLatestSummary = async () => {
      try {
        const res = await axios.get('/api/latest_summary');
        setLatestSummary(res.data);
      } catch (err) {
        console.log('Could not fetch latest summary for landing page');
      } finally {
        setLoading(false);
      }
    };

    fetchLatestSummary();
  }, []);

  const handleAdminClick = () => {
    navigate('/admin');
  };

  const handleLogout = () => {
    onLogout();
  };

  const isFromToday = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date().toDateString();
    const articleDate = new Date(dateStr).toDateString();
    return today === articleDate;
  };

  const isFromThisWeek = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const articleDate = new Date(dateStr);
    return articleDate >= weekAgo && !isFromToday(dateStr);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearchLoading(true);
    try {
      const res = await axios.get(`/api/search_briefings?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchResults(res.data.results || []);
      setShowSearchModal(true);
    } catch (err) {
      console.log('Search failed:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const normalizeStories = (stories, displayType) => {
    if (!Array.isArray(stories)) return [];
    return stories.map(story => ({
      ...story,
      displayType,
      story_type: story.story_type || story.storyType || story.type || 'insight'
    }));
  };

  const deriveFallbackStories = () => {
    const fallbackLocal = [];
    const fallbackNational = [];

    const pushStories = (collection, storyType) => {
      if (!Array.isArray(collection)) return;
      collection.forEach(story => {
        const article = story.article;
        const isLocal = (article?.location?.is_local ?? article?.is_local ?? false);
        const enriched = {
          ...story,
          displayType: isLocal ? 'local' : 'national',
          story_type: story.story_type || storyType || 'insight'
        };
        if (article && article.date) {
          enriched.sortDate = article.date;
        }
        (isLocal ? fallbackLocal : fallbackNational).push(enriched);
      });
    };

    if (latestSummary) {
      pushStories(latestSummary.top_insights, 'insight');
      pushStories(latestSummary.week_stories, 'week');
      pushStories(latestSummary.day_stories, 'day');
    }

    return { fallbackLocal, fallbackNational };
  };

  const dedupeStories = (stories) => {
    const seen = new Set();
    return stories.filter(story => {
      const keyBase = story.article?.id || story.article_id || story.id;
      if (!keyBase) return true;
      const key = `${story.displayType || ''}-${keyBase}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  let localStories = normalizeStories(latestSummary?.local_stories, 'local');
  let nationalStories = normalizeStories(latestSummary?.national_stories, 'national');

  if (localStories.length === 0 && nationalStories.length === 0 && latestSummary) {
    const { fallbackLocal, fallbackNational } = deriveFallbackStories();
    localStories = fallbackLocal;
    nationalStories = fallbackNational;
  }

  localStories = dedupeStories(localStories);
  nationalStories = dedupeStories(nationalStories);

  const sortStoriesByDate = (stories) => {
    return [...stories].sort((a, b) => {
      const dateA = a.article?.date ? new Date(a.article.date) : new Date(0);
      const dateB = b.article?.date ? new Date(b.article.date) : new Date(0);
      return dateB - dateA;
    });
  };

  localStories = sortStoriesByDate(localStories).slice(0, 5);
  nationalStories = sortStoriesByDate(nationalStories);

  const handleBriefingClick = async (briefingId) => {
    setSelectedBriefing(briefingId);
    setShowSearchModal(false);
    setBriefingData(null);
    setShowBriefingModal(true);
    
    try {
      const res = await axios.get(`/api/briefings/${briefingId}`);
      setBriefingData(res.data);
    } catch (err) {
      console.log('Failed to fetch briefing:', err);
      setBriefingData({ error: 'Failed to load briefing data' });
    }
  };

  const closeBriefingModal = () => {
    setShowBriefingModal(false);
    setSelectedBriefing(null);
    setBriefingData(null);
    setSingleStory(null);
  };

  const closeSearchModal = () => {
    setShowSearchModal(false);
    setSearchResults([]);
  };

  const handleStoryClick = (story, storyType = 'insight') => {
    const normalizedType = story?.story_type || storyType || 'insight';
    // Set up single story view for landing page (public view)
    setSingleStory({ story, type: normalizedType, displayType: story?.displayType });
    setShowSearchModal(false);
    setBriefingData(null);
    setSelectedBriefing(null);
    setShowBriefingModal(true);
    
    // Since this is a single story view, we don't need to fetch full briefing data
    // The story already contains all the data we need
    setBriefingData({ 
      ai_title: story.article?.title || story.ai_takeaway_title || 'Industry Story',
      error: null
    });
  };

  return (
    <div className="full-height-container" style={{ backgroundColor: '#ffffff', color: '#333333' }}>
      {/* Header */}
      <header className="site-header site-header-style">
        <div className="container">
          <nav className="navbar navbar-expand-lg navbar-light py-3">
            <div className="d-flex justify-content-between align-items-center w-100">
              <LogoSvg 
                alt="Arkansas Electric Cooperatives, Inc." 
  className="logo-large"
              />
              
              {/* Desktop Menu */}
              <div className="d-none d-lg-flex align-items-center gap-3 ms-auto">
                <ul className="navbar-nav me-4">
                  {authUser && authUser.is_admin && (
                    <li className="nav-item">
                      <button 
                        className="nav-link btn-link text-dark"
                        onClick={handleAdminClick}
                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        Admin Panel
                      </button>
                    </li>
                  )}
                  <li className="nav-item">
                    <button 
                      className="nav-link btn-link text-dark"
                      onClick={() => navigate('/archive')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      Archive
                    </button>
                    </li>
                  {authUser && (
                    <li className="nav-item">
                      <button 
                        className="nav-link btn-link text-dark"
                        onClick={() => navigate('/favorites')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        Favorites
                      </button>
                    </li>
                  )}
                  {authUser && (
                    <li className="nav-item">
                      <button 
                        className="nav-link btn-link text-dark"
                        onClick={handleLogout}
                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        Logout
                      </button>
                    </li>
                  )}
                </ul>
              </div>
              
              {/* Mobile Hamburger Button */}
              <button 
                className="navbar-toggler d-lg-none ms-auto"
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle navigation"
                aria-expanded={mobileMenuOpen}
                style={{ border: 'none', background: 'none', padding: '4px 8px' }}
              >
                <span className="navbar-toggler-icon" style={{ backgroundImage: 'none', marginTop: '10px' }}>
                  <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`} style={{ fontSize: '1.25rem', color: 'var(--green-primary)' }}></i>
                </span>
              </button>
            </div>
            
            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <div className="d-lg-none mt-3">
                <ul className="navbar-nav mobile-menu">
                  {authUser && authUser.is_admin && (
                    <li className="nav-item mobile-menu-item">
                      <button 
                        className="nav-link btn-link w-100 text-start mobile-menu-item"
                        onClick={() => {
                          handleAdminClick();
                          setMobileMenuOpen(false);
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px 0' }}
                      >
                        Admin Panel
                      </button>
                    </li>
                  )}
                  <li className="nav-item mobile-menu-item">
                <button 
                      className="nav-link btn-link w-100 text-start mobile-menu-item"
                      onClick={() => {
                        navigate('/archive');
                        setMobileMenuOpen(false);
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px 0' }}
                >
                  Archive
                </button>
                  </li>
                  {authUser && (
                    <li className="nav-item mobile-menu-item">
                  <button 
                        className="nav-link btn-link w-100 text-start mobile-menu-item"
                        onClick={() => {
                          navigate('/favorites');
                          setMobileMenuOpen(false);
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px 0' }}
                      >
                        Favorites
                  </button>
                    </li>
                )}
                {authUser && (
                    <li className="nav-item mobile-menu-item">
                  <button 
                        className="nav-link btn-link w-100 text-start mobile-menu-item"
                        onClick={() => {
                          handleLogout();
                          setMobileMenuOpen(false);
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px 0' }}
                  >
                    Logout
                  </button>
                    </li>
                )}
                </ul>
              </div>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section section-hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h1 className="hero-title">
                Wire News Digest
              </h1>
              <p className="hero-subtitle">
                Strategic insights from the latest industry analysis
              </p>
            </div>
            <div className="col-lg-6 text-center">
              <ImageCycler 
                images={[
                  "/hero/AECI-DumasHydro_Aerial_3.jpg",
                  "/hero/AECI-McClellan_1.jpg",
                  "/hero/AECI-Power Plant_drone_2_DJI_0168.jpg",
                  "/hero/AECI-Power Plant_drone_5_DJI_0180.jpg",
                  "/hero/arial-hs9.jpg",
                  "/hero/Best CT Shot.JPG",
                  "/hero/ECA_Service_Area_Map_with_highways.jpg",
                  "/hero/ECA-Companies_Overview 2025.jpg",
                  "/hero/Edgemont_Lake_Crossing_1.jpg",
                  "/hero/ENS_AECC_Morris-County_Renderings_Finished_View-NE.png",
                  "/hero/ENS_AECC_Morris-County_Renderings_Finished_View-NW.png",
                  "/hero/ENS_AECC_Morris-County_Renderings_Finished_View-SE.png",
                  "/hero/ENS_AECC_Morris-County_Renderings_Finished_View-SW.png",
                  "/hero/Magnet_Cove_7.jpg",
                  "/hero/Magnet_Cove.jpg",
                  "/hero/Turk_2013_2.jpg"
                ]}
                interval={6000}
                />
            </div>
          </div>
        </div>
      </section>

      {/* Local Stories Section */}
      <section className="section-primary">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">
              LOCAL STORIES
            </h2>
            <p className="section-subtitle">
              Arkansas-focused news and developments
            </p>
          </div>
          
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="row g-4">
              {localStories.length > 0 ? (
                localStories.map((story, index) => (
                  <div key={`local-story-${story.id || index}`} className="col-lg-4 col-md-6">
                    <NewsCard 
                      item={story}
                      isFromToday={isFromToday}
                      isFromThisWeek={isFromThisWeek}
                      type={story.displayType || 'local'}
                      storyKind={story.story_type}
                      onClick={() => handleStoryClick(story, story.story_type)}
                      authUser={authUser}
                    />
                  </div>
                ))
              ) : (
                <div className="col-12 text-center py-5">
                  <div className="text-muted">
                    <i className="fas fa-newspaper fa-3x mb-3"></i>
                    <h4>No Local Stories Available</h4>
                    <p>Check back soon for Arkansas-focused news updates.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* National News Section */}
      <section className="section-day-stories">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">
              NATIONAL NEWS
            </h2>
            <p className="section-subtitle">
              National and international developments affecting the industry
            </p>
          </div>
          
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="row g-4">
              {nationalStories.length > 0 ? (
                nationalStories.map((story, index) => (
                  <div key={`national-story-${story.id || index}`} className="col-lg-4 col-md-6">
                    <NewsCard 
                      item={story}
                      isFromToday={isFromToday}
                      isFromThisWeek={isFromThisWeek}
                      type={story.displayType || 'national'}
                      storyKind={story.story_type}
                      onClick={() => handleStoryClick(story, story.story_type)}
                      authUser={authUser}
                    />
                  </div>
                ))
              ) : (
                <div className="col-12 text-center py-5">
                  <div className="text-muted">
                    <i className="fas fa-globe fa-3x mb-3"></i>
                    <h4>No National News Available</h4>
                    <p>Check back soon for national industry updates.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Search Section */}
      <section className="search-section section-primary">
        <div className="container">
          <div className="text-center mb-4">
            <h2 className="section-title">
              Search Our News Archive
            </h2>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="d-flex">
                <input 
                  type="text" 
                  className="form-control form-control-lg me-3 search-input"
                  placeholder="Enter keywords or phrases..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button 
                  className="btn btn-success btn-lg px-4 btn-search"
                  onClick={handleSearch}
                  disabled={searchLoading || !searchQuery.trim()}
                >
                  {searchLoading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                      Searching...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search me-2"></i>Search
                    </>
                  )}
                </button>
              </div>
              <div className="text-center mt-4">
                <div className="d-flex justify-content-center gap-4 flex-wrap">
                  <button 
                    onClick={() => navigate('/archive')}
                    className="btn btn-outline-primary px-4 py-2"
                  >
                    Visit the Archive
                  </button>
                  <button 
                    onClick={() => window.print()} 
                    className="print-button"
                  >
                    Print as PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RSS Feed Section */}
      <section className="section-secondary">
        <div className="container">
          <div className="text-center mb-4">
            <h2 className="section-title">
              Live Headlines
            </h2>
          </div>
          <div className="rss-feed-container rss-feed-content">
            <ElfsightWidget />
          </div>
        </div>
      </section>



      {/* Search Results Modal */}
      {showSearchModal && (
        <div className="modal show d-block modal-overlay" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Search Results</h5>
                <button 
                  type="button" 
                  className="modal-close-button" 
                  onClick={closeSearchModal}
                  aria-label="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                {searchResults.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-search fa-3x text-muted mb-3"></i>
                    <h5>No results found</h5>
                    <p className="text-muted">Try different keywords or phrases</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-muted mb-3">Found {searchResults.length} briefing(s) matching "{searchQuery}"</p>
                    <div className="list-group">
                      {searchResults.map((result) => (
                        <button
                          key={result.id}
                          className="list-group-item list-group-item-action search-results-clickable"
                          onClick={() => handleBriefingClick(result.id)}
                        >
                          <div className="d-flex w-100 justify-content-between">
                            <h6 className="mb-1">{result.ai_title}</h6>
                            <small className="text-muted">
                              {result.generated_at ? new Date(result.generated_at).toLocaleDateString() : 'No date'}
                            </small>
                          </div>
                          <div className="d-flex gap-3 mt-2">
                            <small className="text-muted">
                              <i className="fas fa-lightbulb me-1"></i>
                              {result.insights_count} insights
                            </small>
                            <small className="text-muted">
                              <i className="fas fa-calendar-week me-1"></i>
                              {result.week_stories_count} weekly
                            </small>
                            <small className="text-muted">
                              <i className="fas fa-calendar-day me-1"></i>
                              {result.day_stories_count} daily
                            </small>
                            {shouldShowHasErrors(result.has_error, result.error) && (
                              <small className="text-warning">
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                Has errors
                              </small>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeSearchModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Briefing Modal */}
      {showBriefingModal && (
        <EnhancedBriefingModal
          show={showBriefingModal}
          briefingData={briefingData}
          summaryId={selectedBriefing}
          singleStory={singleStory}
          isPublic={true}
          authUser={authUser}
          onClose={closeBriefingModal}
        />
      )}
    </div>
  );
};

export default Landing;